// src/components/quickcreate/forms/QuickCreateVendor.jsx
import { useState } from "react";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
// D-291 (P3b): saves the GSTIN as gst_number (same field as Vendor Master -
// before P3b this form saved `gst`, which POs never read). Optional; if
// typed it must be a valid GSTIN. "Unregistered vendor" tick = gst_registered false.
import { validateGstin } from "../../../utils/gstBill";

export default function QuickCreateVendor({ companyId, onCreated, onCancel }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    vendor_name: "",
    phone:       "",
    email:       "",
    gst_number:  "",
    gst_unregistered: false,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSave = async () => {
    if (!form.vendor_name.trim()) { setError("Vendor name required"); return; }
    if (!form.phone.trim())       { setError("Phone required"); return; }

    // gst_registered: true = valid GSTIN, false = Unregistered, null = not entered yet
    let gstNumber = "";
    let gstRegistered = null;
    if (form.gst_unregistered) {
      gstRegistered = false;
    } else if (form.gst_number.trim()) {
      const check = validateGstin(form.gst_number);
      if (!check.ok) { setError(check.reason); return; }
      gstNumber = check.gstin;
      gstRegistered = true;
    }

    setSaving(true);
    try {
      // Duplicate check
      const dupSnap = await getDocs(query(
        collection(db, "vendors"),
        where("company_id",  "==", companyId),
        where("vendor_name", "==", form.vendor_name.trim())
      ));
      if (!dupSnap.empty) {
        setError(`"${form.vendor_name.trim()}" already exists`);
        setSaving(false);
        return;
      }

      const docRef = await addDoc(collection(db, "vendors"), {
        company_id:      companyId,
        vendor_name:     form.vendor_name.trim(),
        phone:           form.phone.trim(),
        email:           form.email.trim(),
        gst_number:      gstNumber,
        gst_registered:  gstRegistered,
        is_active:       true,
        created_at:      serverTimestamp(),
        created_by:      user.uid || user.id || "",
        created_by_name: user.name || "",
      });

      onCreated({
        id:          docRef.id,
        vendor_name: form.vendor_name.trim(),
        phone:       form.phone.trim(),
        email:       form.email.trim(),
        gst_number:  gstNumber,
        gst_registered: gstRegistered,
        gst:         gstNumber, // kept for any caller that still reads .gst from this object
        company_id:  companyId,
      });

    } catch (err) {
      console.error("QuickCreateVendor error:", err);
      setError(err.message || "Failed to create vendor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">
        Basic details only. Full profile Vendor Master mein edit kar sakte ho.
      </p>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Vendor Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Sharma Traders"
          value={form.vendor_name}
          onChange={e => set("vendor_name", e.target.value)}
          autoFocus
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Phone <span className="text-red-400">*</span>
        </label>
        <input
          type="tel"
          placeholder="9876543210"
          value={form.phone}
          onChange={e => set("phone", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Email <span className="text-slate-300">(optional)</span>
        </label>
        <input
          type="email"
          placeholder="vendor@email.com"
          value={form.email}
          onChange={e => set("email", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          GSTIN <span className="text-slate-300">(optional)</span>
        </label>
        <input
          type="text"
          placeholder={form.gst_unregistered ? "Unregistered vendor" : "15 characters, e.g. 09AAQFB2219L1Z5"}
          value={form.gst_unregistered ? "" : form.gst_number}
          onChange={e => set("gst_number", e.target.value.toUpperCase())}
          disabled={form.gst_unregistered}
          maxLength={15}
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-slate-50"
        />
        {!form.gst_unregistered && form.gst_number.trim() && (() => {
          const check = validateGstin(form.gst_number);
          return (
            <p className={`text-xs mt-1 ${check.ok ? "text-green-600" : "text-red-500"}`}>
              {check.ok ? `Valid GSTIN · state code ${check.state_code}` : check.reason}
            </p>
          );
        })()}
        <label className="flex items-center gap-2 mt-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={form.gst_unregistered}
            onChange={e => set("gst_unregistered", e.target.checked)}
          />
          Unregistered vendor (no GSTIN)
        </label>
      </div>

      {error && (
        <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 font-medium hover:bg-slate-50 transition disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition disabled:opacity-40"
        >
          {saving ? "Saving..." : "Create Vendor"}
        </button>
      </div>
    </div>
  );
}